import React, { useState, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { Icon } from "./Icons";

const token = () => sessionStorage.getItem("jwtToken");
const hdr = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token()}`
});

/**
 * Calculates current, next, and historical quarters.
 * e.g. for Oct 2026 -> Q4 2026 is current, Q1 2027 is NEXT quarter.
 */
const getQuarterOptions = () => {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month(); // 0 - 11
  const currentQ = Math.floor(currentMonth / 3) + 1; // 1 - 4

  const nextQ = currentQ === 4 ? 1 : currentQ + 1;
  const nextQYear = currentQ === 4 ? currentYear + 1 : currentYear;

  const quarters = [
    { key: `Q${currentQ} ${currentYear}`, label: `Q${currentQ} ${currentYear} (Current Quarter)`, isCurrent: true },
    { key: `Q${nextQ} ${nextQYear}`, label: `Q${nextQ} ${nextQYear} (Next Quarter)`, isNext: true },
    { key: "ALL", label: "All Quarters" },
  ];

  // Add 3 future quarters
  for (let i = 1; i <= 3; i++) {
    let q = nextQ + i;
    let y = nextQYear;
    while (q > 4) { q -= 4; y += 1; }
    quarters.push({ key: `Q${q} ${y}`, label: `Q${q} ${y}` });
  }

  // Add 3 past quarters
  for (let i = 1; i <= 3; i++) {
    let q = currentQ - i;
    let y = currentYear;
    while (q < 1) { q += 4; y -= 1; }
    quarters.push({ key: `Q${q} ${y}`, label: `Q${q} ${y}` });
  }

  const seen = new Set();
  return quarters.filter(q => {
    if (seen.has(q.key)) return false;
    seen.add(q.key);
    return true;
  });
};

const WORK_TYPE_COLORS = {
  BUSINESS_WORK: { bg: "#EFF6FF", text: "#2563EB", border: "#DBEAFE", label: "Business Work", dot: "#3B82F6" },
  UPSKILLING:    { bg: "#FAF5FF", text: "#7C3AED", border: "#E9D5FF", label: "Upskilling", dot: "#8B5CF6" },
};

const getWorkTypeColor = (type) => {
  if (!type) return WORK_TYPE_COLORS.BUSINESS_WORK;
  const upper = String(type).toUpperCase();
  if (upper.includes("UPSKILL")) {
    return WORK_TYPE_COLORS.UPSKILLING;
  }
  return WORK_TYPE_COLORS.BUSINESS_WORK;
};

const STATUS_COLORS = {
  ASSIGNED:    { bg: "#EFF6FF", text: "#2563EB", border: "#DBEAFE" },
  IN_PROGRESS: { bg: "#FFFBEB", text: "#D97706", border: "#FDE68A" },
  COMPLETED:   { bg: "#ECFDF5", text: "#059669", border: "#A7F3D0" },
  REVIEWED:    { bg: "#F5F3FF", text: "#7C3AED", border: "#DDD6FE" },
  NEEDS_IMPROVEMENT: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
};

const RATING_LABELS = {
  1: "1 - Needs Improvement",
  2: "2 - Below Expectations",
  3: "3 - Meets Expectations",
  4: "4 - Exceeds Expectations",
  5: "5 - Outstanding",
};

const EMPLOYEES_PER_PAGE = 4;

export const TargetsAppraisalsView = ({ activeEmployees = [], clients = [] }) => {
  const validActiveEmployees = useMemo(() => {
    return (activeEmployees || []).filter(e => e && e.active !== false);
  }, [activeEmployees]);

  const activeClients = useMemo(() => {
    return (clients || []).filter(c => c && c.active !== false && c.fullName !== "Internal");
  }, [clients]);

  const quarterOptions = useMemo(() => getQuarterOptions(), []);
  // Default to Current Quarter
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const current = quarterOptions.find(q => q.isCurrent);
    return current ? current.key : quarterOptions[0].key;
  });
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("ALL");
  const [projectFilter, setProjectFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [workTypeFilter, setWorkTypeFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState(null);

  // Modals state
  const [assignModal, setAssignModal] = useState({ isOpen: false, target: null, defaultUsername: "" });
  const [reviewModal, setReviewModal] = useState({ isOpen: false, target: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, targetId: null, title: "" });

  // Form states
  const [form, setForm] = useState({
    username: "",
    quarter: "",
    title: "",
    description: "",
    kpi: "",
    workType: "BUSINESS_WORK",
    status: "ASSIGNED",
    dueDate: "",
  });

  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    feedback: "",
    status: "REVIEWED",
  });

  const [submitting, setSubmitting] = useState(false);

  // Fetch targets for the selected quarter
  const fetchTargets = useCallback(async (quarter, isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    try {
      const qParam = (quarter && quarter !== "ALL") ? `?quarter=${encodeURIComponent(quarter)}` : "";
      const res = await fetch(`/timesheets/appraisals${qParam}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTargets(Array.isArray(data) ? data : []);
      } else {
        setTargets([]);
      }
    } catch (e) {
      console.error("Error fetching targets:", e);
      setTargets([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchTargets(selectedQuarter);
  }, [selectedQuarter, fetchTargets]);

  // Auto-dismiss alert popup after 3.5 seconds
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => {
      setMessage(null);
    }, 3500);
    return () => clearTimeout(timer);
  }, [message]);

  // Open Assign / Edit Modal
  const openAssignModal = (target = null, defaultUsername = "") => {
    if (target) {
      setForm({
        id: target.id,
        username: target.username || "",
        quarter: target.quarter || selectedQuarter,
        title: target.title || "",
        description: target.description || "",
        kpi: target.kpi || "",
        workType: (target.workType || target.priority || "").toUpperCase().includes("UPSKILL") ? "UPSKILLING" : "BUSINESS_WORK",
        status: target.status || "ASSIGNED",
        dueDate: target.dueDate || "",
      });
    } else {
      setForm({
        username: defaultUsername || (validActiveEmployees[0]?.username || ""),
        quarter: selectedQuarter !== "ALL" ? selectedQuarter : (quarterOptions.find(q => q.isCurrent)?.key || "Q4 2026"),
        title: "",
        description: "",
        kpi: "",
        workType: "BUSINESS_WORK",
        status: "ASSIGNED",
        dueDate: "",
      });
    }
    setAssignModal({ isOpen: true, target, defaultUsername });
  };

  // Open Review / Appraisal Modal
  const openReviewModal = (target) => {
    setReviewForm({
      rating: target.rating > 0 ? target.rating : 5,
      feedback: target.appraisalFeedback || "",
      status: target.status === "REVIEWED" ? "REVIEWED" : "REVIEWED",
    });
    setReviewModal({ isOpen: true, target });
  };

  // Save Target
  const handleSaveTarget = async (e) => {
    e.preventDefault();
    const isUpskilling = (form.workType || "").toUpperCase().includes("UPSKILL");

    if (!form.username) {
      setMessage({ type: "error", text: "Please select an employee." });
      return;
    }

    if (!form.title.trim() && !isUpskilling) {
      setMessage({ type: "error", text: "Please enter a target title / project." });
      return;
    }

    if (isUpskilling && !form.title.trim() && !form.description.trim()) {
      setMessage({ type: "error", text: "Please provide a description of what skills they are learning." });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    const resolvedTitle = form.title.trim()
      || (form.description.trim()
        ? (form.description.trim().length > 60 ? form.description.trim().slice(0, 57) + "..." : form.description.trim())
        : "Upskilling & Learning");

    try {
      const isEdit = !!form.id;
      const url = isEdit ? `/timesheets/appraisals/${form.id}` : "/timesheets/appraisals/assign";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: hdr(),
        body: JSON.stringify({
          id: form.id,
          username: form.username,
          quarter: form.quarter || selectedQuarter,
          title: resolvedTitle,
          description: form.description.trim(),
          kpi: form.kpi.trim(),
          workType: form.workType || "BUSINESS_WORK",
          priority: form.workType || "BUSINESS_WORK",
          status: form.status || "ASSIGNED",
          dueDate: form.dueDate || null,
        }),
      });

      if (res.ok) {
        const saved = await res.json();
        setTargets(prev => {
          if (isEdit) {
            return prev.map(t => t.id === saved.id ? saved : t);
          }
          return [saved, ...prev];
        });
        if (!isEdit) {
          setCurrentPage(1);
        }
        setAssignModal({ isOpen: false, target: null, defaultUsername: "" });
        setMessage({ type: "success", text: `Target ${isEdit ? "updated" : "assigned"} successfully!` });
      } else {
        const errText = await res.text();
        setMessage({ type: "error", text: errText || "Failed to save target." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "An error occurred while saving target." });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Appraisal Review
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewModal.target) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await fetch(`/timesheets/appraisals/${reviewModal.target.id}/review`, {
        method: "PATCH",
        headers: hdr(),
        body: JSON.stringify({
          rating: reviewForm.rating,
          feedback: reviewForm.feedback.trim(),
          status: "REVIEWED",
        }),
      });

      if (res.ok) {
        const updated = await res.json();
        setTargets(prev => prev.map(t => t.id === updated.id ? updated : t));
        setReviewModal({ isOpen: false, target: null });
        setMessage({ type: "success", text: "Appraisal review submitted successfully!" });
      } else {
        const errText = await res.text();
        setMessage({ type: "error", text: errText || "Failed to submit appraisal review." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "An error occurred while submitting review." });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Target
  const handleDeleteTarget = async () => {
    if (!deleteModal.targetId) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/timesheets/appraisals/${deleteModal.targetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        setTargets(prev => prev.filter(t => t.id !== deleteModal.targetId));
        setDeleteModal({ isOpen: false, targetId: null, title: "" });
        setMessage({ type: "success", text: "Target deleted successfully." });
      } else {
        setMessage({ type: "error", text: "Failed to delete target." });
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "An error occurred while deleting target." });
    } finally {
      setSubmitting(false);
    }
  };

  // Group targets by username for active employees only (case-insensitive & trimmed)
  const targetsByEmployee = useMemo(() => {
    const map = {};
    const activeUsernames = new Set();
    validActiveEmployees.forEach(emp => {
      const u = (emp.username || "").trim().toLowerCase();
      if (u) {
        map[u] = [];
        activeUsernames.add(u);
      }
    });

    targets.forEach(t => {
      const u = (t.username || "").trim().toLowerCase();
      // Only include targets for currently active employees
      if (u && activeUsernames.has(u)) {
        map[u].push(t);
      }
    });

    // Ensure targets for each employee are strictly ordered latest-added first
    Object.keys(map).forEach(u => {
      map[u].sort((a, b) => {
        const timeA = a.assignedAt ? new Date(a.assignedAt).getTime() : 0;
        const timeB = b.assignedAt ? new Date(b.assignedAt).getTime() : 0;
        if (timeB !== timeA) return timeB - timeA;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      });
    });

    return map;
  }, [validActiveEmployees, targets]);

  // Only active employees are shown in this tab
  const allEmployees = validActiveEmployees;

  // Active targets belonging only to active employees
  const activeTargets = useMemo(() => {
    const activeUsernames = new Set(validActiveEmployees.map(e => (e.username || "").trim().toLowerCase()));
    return (targets || []).filter(t => {
      const u = (t.username || "").trim().toLowerCase();
      return u && activeUsernames.has(u);
    });
  }, [targets, validActiveEmployees]);

  // Distinct project titles from targets of active employees
  const distinctProjects = useMemo(() => {
    const list = new Set();
    activeTargets.forEach(t => {
      const title = (t?.title || "").trim();
      if (title) list.add(title);
    });
    return Array.from(list);
  }, [activeTargets]);

  // Filtered & sorted employees list (whoever adds their task latest shows first)
  const filteredEmployees = useMemo(() => {
    const list = allEmployees.filter(emp => {
      const empKey = (emp.username || "").trim().toLowerCase();
      const empTargets = targetsByEmployee[empKey] || [];

      // Client filter
      const empClient = emp.clientName || "Internal";
      if (clientFilter !== "ALL" && empClient.toLowerCase() !== clientFilter.toLowerCase()) {
        return false;
      }

      // Project filter
      if (projectFilter !== "ALL") {
        const matchesProject = empTargets.some(t =>
          (t.title || "").trim().toLowerCase() === projectFilter.toLowerCase()
        );
        if (!matchesProject) return false;
      }

      // Status filter
      if (statusFilter === "NOT_ASSIGNED" && empTargets.length > 0) return false;
      if (statusFilter !== "ALL" && statusFilter !== "NOT_ASSIGNED") {
        if (!empTargets.some(t => t.status === statusFilter)) return false;
      }

      // Work Type filter
      if (workTypeFilter !== "ALL") {
        const matchesWorkType = empTargets.some(t => {
          const itemType = (t.workType || t.priority || "").toUpperCase().includes("UPSKILL") ? "UPSKILLING" : "BUSINESS_WORK";
          return itemType === workTypeFilter;
        });
        if (!matchesWorkType) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = (emp.fullName || "").toLowerCase().includes(q) || (emp.username || "").toLowerCase().includes(q);
        const matchesClient = (empClient || "").toLowerCase().includes(q);
        const matchesTarget = empTargets.some(t => (t.title || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q));
        if (!matchesName && !matchesClient && !matchesTarget) return false;
      }

      return true;
    });

    const getEmployeeLatestScore = (emp) => {
      const empKey = (emp.username || "").trim().toLowerCase();
      const empTargets = targetsByEmployee[empKey] || [];
      if (empTargets.length === 0) {
        return { hasTargets: false, maxTime: 0, maxId: 0, count: 0 };
      }
      let maxTime = 0;
      let maxId = 0;
      empTargets.forEach(t => {
        const time = t.assignedAt ? new Date(t.assignedAt).getTime() : 0;
        if (time > maxTime) maxTime = time;
        const id = Number(t.id) || 0;
        if (id > maxId) maxId = id;
      });
      return { hasTargets: true, maxTime, maxId, count: empTargets.length };
    };

    // Sort employees: whoever adds their task the latest added shows first (above)
    list.sort((a, b) => {
      const scoreA = getEmployeeLatestScore(a);
      const scoreB = getEmployeeLatestScore(b);

      // Employees with targets show before employees without targets
      if (scoreA.hasTargets && !scoreB.hasTargets) return -1;
      if (!scoreA.hasTargets && scoreB.hasTargets) return 1;

      // Both have targets: employee whose task was added most recently shows first
      if (scoreA.hasTargets && scoreB.hasTargets) {
        if (scoreB.maxTime !== scoreA.maxTime) {
          return scoreB.maxTime - scoreA.maxTime;
        }
        if (scoreB.maxId !== scoreA.maxId) {
          return scoreB.maxId - scoreA.maxId;
        }
      }

      // Tie-breaker or both have no targets: alphabetical
      const nameA = (a.fullName || a.username || "").toLowerCase();
      const nameB = (b.fullName || b.username || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return list;
  }, [allEmployees, clientFilter, projectFilter, statusFilter, workTypeFilter, searchQuery, targetsByEmployee]);

  // Reset pagination when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedQuarter, clientFilter, projectFilter, statusFilter, workTypeFilter, searchQuery]);

  // Pagination calculations (4 employees per page)
  const totalPages = Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE) || 1;
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const pagedEmployees = useMemo(() => {
    const start = (safeCurrentPage - 1) * EMPLOYEES_PER_PAGE;
    return filteredEmployees.slice(start, start + EMPLOYEES_PER_PAGE);
  }, [filteredEmployees, safeCurrentPage]);

  // Metrics (computed solely for active headcount and active employee targets)
  const totalEmployeesCount = validActiveEmployees.length;
  const assignedCount = Object.values(targetsByEmployee).filter(list => list.length > 0).length;
  const unassignedCount = Math.max(0, totalEmployeesCount - assignedCount);
  const reviewedTargets = activeTargets.filter(t => t.rating > 0);
  const avgRating = reviewedTargets.length > 0
    ? (reviewedTargets.reduce((s, t) => s + t.rating, 0) / reviewedTargets.length).toFixed(1)
    : "—";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* ── HEADER CARD ── */}
      <div className="em-card" style={{ padding: "20px 24px", background: "linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 100%)", border: "1px solid #E2EEFF" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563EB" }}>
                <Icon.Target size={18} />
              </div>
              <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Targets & Appraisals</h3>
            </div>
            <p style={{ fontSize: "0.8rem", color: "#64748B", margin: 0 }}>
              Assign quarterly work, set goals & KPIs, and review employee performance for upcoming and active quarters.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {/* Quarter Selector */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1.5px solid #E2EEFF", borderRadius: 10, padding: "6px 12px", boxShadow: "0 1px 3px rgba(37,99,235,0.05)" }}>
              <Icon.Calendar size={14} color="#64748B" />
              <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>Quarter:</label>
              <select
                value={selectedQuarter}
                onChange={e => setSelectedQuarter(e.target.value)}
                style={{ border: "none", background: "transparent", fontSize: "0.84rem", fontWeight: 700, color: "#2563EB", outline: "none", cursor: "pointer" }}
              >
                {quarterOptions.map(q => (
                  <option key={q.key} value={q.key}>{q.label}</option>
                ))}
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => fetchTargets(selectedQuarter, true)}
              disabled={refreshing}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 14px", borderRadius: 10,
                background: "#fff", border: "1.5px solid #E2EEFF",
                color: "#2563EB", fontSize: "0.82rem", fontWeight: 600,
                cursor: "pointer", transition: "all 0.15s ease",
              }}
              title="Refresh targets & work assignments"
            >
              <Icon.Clock size={13} color="#2563EB" />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>

            {/* Quick Assign Button */}
            <button
              onClick={() => openAssignModal(null)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 16px", borderRadius: 10, border: "none",
                background: "#2563EB", color: "#fff",
                fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
                boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                transition: "all 0.15s"
              }}
            >
              <Icon.Plus size={14} /> Assign Work
            </button>
          </div>
        </div>

        {/* ── METRICS OVERVIEW ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 18 }}>
          <div style={{ background: "#fff", padding: "12px 16px", borderRadius: 10, border: "1px solid #E2EEFF" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase" }}>Total Employees</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#0F172A", marginTop: 4 }}>{totalEmployeesCount}</div>
            <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginTop: 2 }}>Active organization headcount</div>
          </div>

          <div style={{ background: "#fff", padding: "12px 16px", borderRadius: 10, border: "1px solid #E2EEFF" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase" }}>Work Assigned ({selectedQuarter})</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#2563EB", marginTop: 4 }}>
              {assignedCount} <span style={{ fontSize: "0.8rem", color: "#64748B", fontWeight: 500 }}>/ {totalEmployeesCount}</span>
            </div>
            <div style={{ fontSize: "0.7rem", color: "#10B981", fontWeight: 600, marginTop: 2 }}>
              {totalEmployeesCount > 0 ? Math.round((assignedCount / totalEmployeesCount) * 100) : 0}% assigned
            </div>
          </div>

          <div style={{ background: "#fff", padding: "12px 16px", borderRadius: 10, border: "1px solid #E2EEFF" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase" }}>Pending Work Assignment</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: unassignedCount > 0 ? "#F59E0B" : "#10B981", marginTop: 4 }}>
              {unassignedCount}
            </div>
            <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginTop: 2 }}>
              {unassignedCount > 0 ? "Needs work setup for " + selectedQuarter : "All employees assigned"}
            </div>
          </div>

          <div style={{ background: "#fff", padding: "12px 16px", borderRadius: 10, border: "1px solid #E2EEFF" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", textTransform: "uppercase" }}>Appraisal Reviews</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#7C3AED", marginTop: 4 }}>
              {reviewedTargets.length} <span style={{ fontSize: "0.8rem", color: "#64748B", fontWeight: 500 }}>{avgRating !== "—" ? `· Avg ⭐ ${avgRating}` : ""}</span>
            </div>
            <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginTop: 2 }}>Completed performance reviews</div>
          </div>
        </div>
      </div>

      {/* ── NOTIFICATION MESSAGE ── */}
      {message && (
        <div style={{
          padding: "10px 16px", borderRadius: 8, fontSize: "0.82rem", fontWeight: 600,
          background: message.type === "success" ? "#ECFDF5" : "#FEF2F2",
          color: message.type === "success" ? "#059669" : "#DC2626",
          border: `1px solid ${message.type === "success" ? "#A7F3D0" : "#FECACA"}`,
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* ── SEARCH & FILTER CONTROLS ── */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", flex: 1 }}>
          {/* Search Box */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "#fff", border: "1.5px solid #E2EEFF", borderRadius: 10, minWidth: 260, flex: "1 1 260px" }}>
            <Icon.Search size={14} color="#94A3B8" />
            <input
              type="text"
              placeholder="Search employee or target..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ border: "none", outline: "none", fontSize: "0.82rem", color: "#0F172A", width: "100%", background: "transparent" }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "0.75rem" }}>✕</button>
            )}
          </div>

          {/* Client Filter */}
          <select
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            style={{ padding: "8px 14px", borderRadius: 10, border: "1.5px solid #E2EEFF", background: "#fff", fontSize: "0.82rem", fontWeight: 600, color: "#0F172A", outline: "none", cursor: "pointer" }}
            title="Filter by Client"
          >
            <option value="ALL">All Clients</option>
            <option value="Internal">Internal</option>
            {activeClients.map(c => (
              <option key={c.username} value={c.fullName}>{c.fullName}</option>
            ))}
          </select>

          {/* Project Filter */}
          <select
            value={projectFilter}
            onChange={e => setProjectFilter(e.target.value)}
            style={{ padding: "8px 14px", borderRadius: 10, border: "1.5px solid #E2EEFF", background: "#fff", fontSize: "0.82rem", fontWeight: 600, color: "#0F172A", outline: "none", cursor: "pointer" }}
            title="Filter by Project"
          >
            <option value="ALL">All Projects</option>
            {distinctProjects.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: "8px 14px", borderRadius: 10, border: "1.5px solid #E2EEFF", background: "#fff", fontSize: "0.82rem", fontWeight: 600, color: "#0F172A", outline: "none", cursor: "pointer" }}
          >
            <option value="ALL">All Statuses</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="REVIEWED">Reviewed & Appraised</option>
            <option value="NOT_ASSIGNED">Not Assigned Yet</option>
          </select>

          {/* Work Type Filter */}
          <select
            value={workTypeFilter}
            onChange={e => setWorkTypeFilter(e.target.value)}
            style={{ padding: "8px 14px", borderRadius: 10, border: "1.5px solid #E2EEFF", background: "#fff", fontSize: "0.82rem", fontWeight: 600, color: "#0F172A", outline: "none", cursor: "pointer" }}
          >
            <option value="ALL">All Work Types</option>
            <option value="BUSINESS_WORK">💼 Business Work</option>
            <option value="UPSKILLING">🚀 Upskilling</option>
          </select>
        </div>

        <div style={{ fontSize: "0.8rem", color: "#64748B", fontWeight: 600 }}>
          Showing {filteredEmployees.length === 0 ? 0 : `${(safeCurrentPage - 1) * EMPLOYEES_PER_PAGE + 1}–${Math.min(safeCurrentPage * EMPLOYEES_PER_PAGE, filteredEmployees.length)}`} of {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? "s" : ""}
          {filteredEmployees.length !== activeEmployees.length && ` (filtered from ${activeEmployees.length})`}
        </div>
      </div>

      {/* ── EMPLOYEE TARGET LIST ── */}
      {loading ? (
        <div className="em-card" style={{ padding: "40px 20px", textAlign: "center", color: "#64748B" }}>
          <p>Loading targets & work assignments for {selectedQuarter}...</p>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="em-card" style={{ padding: "40px 20px", textAlign: "center", color: "#94A3B8" }}>
          <Icon.Users size={32} color="#CBD5E1" />
          <p style={{ marginTop: 8, fontSize: "0.88rem" }}>No employees match the selected filters.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {pagedEmployees.map(emp => {
            const empKey = (emp.username || "").trim().toLowerCase();
            const empTargets = targetsByEmployee[empKey] || [];
            const hasTargets = empTargets.length > 0;

            return (
              <div
                key={emp.username}
                className="em-card"
                style={{
                  padding: "16px 20px",
                  border: hasTargets ? "1px solid #E2EEFF" : "1.5px dashed #CBD5E1",
                  background: hasTargets ? "#fff" : "#FAFCFF",
                  borderRadius: 12,
                  boxShadow: hasTargets ? "0 1px 4px rgba(0,0,0,0.03)" : "none",
                  transition: "all 0.15s"
                }}
              >
                {/* Employee Row Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, paddingBottom: hasTargets ? 12 : 0, borderBottom: hasTargets ? "1px solid #F1F5F9" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="em-emp-avatar" style={{ width: 36, height: 36, fontSize: "0.9rem" }}>
                      {(emp.fullName || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A" }}>{emp.fullName}</span>
                        <span style={{ fontSize: "0.72rem", color: "#64748B", background: "#F1F5F9", padding: "2px 6px", borderRadius: 6 }}>{emp.username}</span>
                        <span style={{ fontSize: "0.72rem", color: "#2563EB", background: "#EFF6FF", padding: "2px 8px", borderRadius: 999, fontWeight: 600 }}>
                          {emp.clientName || "Internal"}
                        </span>
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginTop: 2 }}>
                        {empTargets.length} target{empTargets.length !== 1 ? "s" : ""} assigned for {selectedQuarter}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => openAssignModal(null, emp.username)}
                    style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "5px 10px", borderRadius: 8,
                      border: "1px solid #DBEAFE", background: "#EFF6FF",
                      color: "#2563EB", fontSize: "0.74rem", fontWeight: 700,
                      cursor: "pointer", transition: "all 0.15s ease",
                    }}
                    title={`Assign new work or target to ${emp.fullName}`}
                  >
                    <Icon.Plus size={12} /> Assign Work
                  </button>
                </div>

                {/* Target Items List */}
                {hasTargets ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                    {empTargets
                      .filter(target => {
                        if (projectFilter === "ALL") return true;
                        const empClient = emp.clientName || "Internal";
                        if (empClient.toLowerCase() === projectFilter.toLowerCase()) return true;
                        return (target.title || "").trim().toLowerCase() === projectFilter.toLowerCase();
                      })
                      .map(target => {
                      const workTypeStyle = getWorkTypeColor(target.workType || target.priority);
                      const statusStyle = STATUS_COLORS[target.status] || STATUS_COLORS.ASSIGNED;

                      return (
                        <div
                          key={target.id}
                          style={{
                            padding: "14px 16px",
                            background: "#F8FBFF",
                            border: "1px solid #E2EEFF",
                            borderRadius: 10,
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {/* Target Title & Badges */}
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <h5 style={{ fontSize: "0.88rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>
                                {target.title}
                              </h5>
                              <span style={{
                                fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                                background: workTypeStyle.bg, color: workTypeStyle.text, border: `1px solid ${workTypeStyle.border}`,
                                display: "inline-flex", alignItems: "center", gap: 4
                              }}>
                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: workTypeStyle.dot }} />
                                {workTypeStyle.label}
                              </span>
                              <span style={{
                                fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                                background: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.border}`
                              }}>
                                {target.status.replace(/_/g, " ")}
                              </span>
                              {(target.assignedBy || "").toLowerCase().startsWith("self") && (
                                <span style={{
                                  fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                                  background: "#FAF5FF", color: "#7C3AED", border: "1px solid #E9D5FF"
                                }}>
                                  👤 Self-Added
                                </span>
                              )}
                              {target.dueDate && (
                                <span style={{ fontSize: "0.72rem", color: "#64748B", display: "flex", alignItems: "center", gap: 4 }}>
                                  <Icon.Clock size={11} color="#64748B" /> Due: {dayjs(target.dueDate).format("MMM D, YYYY")}
                                </span>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {(target.status === "COMPLETED" || target.status === "REVIEWED" || target.rating > 0) && (
                                <button
                                  onClick={() => openReviewModal(target)}
                                  title="Appraisal & Performance Review"
                                  style={{
                                    padding: "4px 10px", borderRadius: 6, border: "1px solid #DDD6FE",
                                    background: "#F5F3FF", color: "#7C3AED", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 4
                                  }}
                                >
                                  <Icon.Star size={11} /> Appraisal
                                </button>
                              )}
                              <button
                                onClick={() => openAssignModal(target)}
                                title="Edit Target"
                                style={{
                                  padding: "4px 8px", borderRadius: 6, border: "1px solid #E2EEFF",
                                  background: "#fff", color: "#475569", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer"
                                }}
                              >
                                <Icon.Edit size={12} />
                              </button>
                              <button
                                onClick={() => setDeleteModal({ isOpen: true, targetId: target.id, title: target.title })}
                                title="Delete Target"
                                style={{
                                  padding: "4px 8px", borderRadius: 6, border: "1px solid #FECACA",
                                  background: "#FEF2F2", color: "#DC2626", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer"
                                }}
                              >
                                <Icon.X size={12} />
                              </button>
                            </div>
                          </div>

                          {/* Description / Scope / Skills */}
                          {target.description && (
                            <div style={{ fontSize: "0.78rem", color: "#334155", lineHeight: 1.45, background: "#fff", padding: "8px 12px", borderRadius: 6, border: "1px solid #E2EEFF" }}>
                              <strong>{(target.workType || target.priority || "").toUpperCase().includes("UPSKILL") ? "Skills Learning:" : "Work Scope:"}</strong> {target.description}
                            </div>
                          )}

                          {/* KPIs / Deliverables */}
                          {target.kpi && (
                            <div style={{ fontSize: "0.76rem", color: "#475569", lineHeight: 1.4, paddingLeft: 4 }}>
                              <strong style={{ color: "#0F172A" }}>{(target.workType || target.priority || "").toUpperCase().includes("UPSKILL") ? "Learning Outcomes & KPIs:" : "Key Deliverables & KPIs:"}</strong>
                              <div style={{ whiteSpace: "pre-line", marginTop: 2 }}>{target.kpi}</div>
                            </div>
                          )}

                          {/* Appraisal Rating & Feedback Banner */}
                          {target.rating > 0 && (
                            <div style={{
                              marginTop: 4, padding: "8px 12px", borderRadius: 8,
                              background: "#FAF5FF", border: "1px solid #E9D5FF",
                              display: "flex", flexDirection: "column", gap: 4
                            }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#6B21A8", display: "flex", alignItems: "center", gap: 4 }}>
                                  <Icon.Award size={13} color="#7C3AED" /> Appraisal Score: {"⭐".repeat(target.rating)} ({target.rating}/5 - {RATING_LABELS[target.rating]?.split(" - ")[1] || ""})
                                </span>
                                {target.reviewedAt && (
                                  <span style={{ fontSize: "0.68rem", color: "#9333EA" }}>
                                    Reviewed on {dayjs(target.reviewedAt).format("MMM D, YYYY")}
                                  </span>
                                )}
                              </div>
                              {target.appraisalFeedback && (
                                <p style={{ margin: 0, fontSize: "0.75rem", color: "#581C87", fontStyle: "italic" }}>
                                  "{target.appraisalFeedback}"
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ marginTop: 10, padding: "12px 14px", background: "#fff", borderRadius: 8, border: "1px solid #F1F5F9", display: "flex", alignItems: "center" }}>
                    <span style={{ fontSize: "0.78rem", color: "#64748B" }}>
                      No targets or work assigned for <strong>{selectedQuarter}</strong> yet.
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── PAGINATION CONTROLS (4 employees per page) ── */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            padding: "14px 20px",
            background: "#fff",
            border: "1px solid #E2EEFF",
            borderRadius: 12,
            fontSize: "0.82rem",
            color: "#64748B",
            boxShadow: "0 1px 4px rgba(0,0,0,0.02)",
          }}
        >
          <span style={{ fontWeight: 600 }}>
            Page {safeCurrentPage} of {totalPages} ({filteredEmployees.length} total employees)
          </span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              disabled={safeCurrentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: "1.5px solid #E2EEFF",
                background: safeCurrentPage <= 1 ? "#F8FAFC" : "#fff",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: safeCurrentPage <= 1 ? "#CBD5E1" : "#2563EB",
                cursor: safeCurrentPage <= 1 ? "not-allowed" : "pointer",
                transition: "all 0.15s ease",
              }}
            >
              ‹ Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: page === safeCurrentPage ? "1.5px solid #2563EB" : "1.5px solid #E2EEFF",
                    background: page === safeCurrentPage ? "#EFF6FF" : "#fff",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: page === safeCurrentPage ? "#2563EB" : "#64748B",
                    cursor: "pointer",
                    minWidth: 34,
                    transition: "all 0.15s ease",
                  }}
                >
                  {page}
                </button>
              );
            })}
            <button
              disabled={safeCurrentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: "1.5px solid #E2EEFF",
                background: safeCurrentPage >= totalPages ? "#F8FAFC" : "#fff",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: safeCurrentPage >= totalPages ? "#CBD5E1" : "#2563EB",
                cursor: safeCurrentPage >= totalPages ? "not-allowed" : "pointer",
                transition: "all 0.15s ease",
              }}
            >
              Next ›
            </button>
          </div>
        </div>
      )}

      {/* ── ASSIGN / EDIT TARGET MODAL ── */}
      {assignModal.isOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(2px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 16
        }}>
          <div style={{
            background: "#fff", borderRadius: 14, width: "100%", maxWidth: 540,
            maxHeight: "90vh",
            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #E2EEFF", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#F8FBFF", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon.Target size={18} color="#2563EB" />
                <h4 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>
                  {assignModal.target ? "Edit Target / Work" : "Assign Quarterly Work"}
                </h4>
              </div>
              <button onClick={() => setAssignModal({ isOpen: false, target: null, defaultUsername: "" })} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "1.1rem" }}>✕</button>
            </div>

            <form
              onSubmit={handleSaveTarget}
              style={{
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                overflowY: "auto",
                maxHeight: "calc(90vh - 65px)"
              }}
            >
              {/* Employee Selection */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Employee *</label>
                <select
                  value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })}
                  disabled={!!assignModal.target}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", background: assignModal.target ? "#F1F5F9" : "#fff", outline: "none" }}
                  required
                >
                  {validActiveEmployees.map(e => (
                    <option key={e.username} value={e.username}>{e.fullName} ({e.username}) — {e.clientName || "Internal"}</option>
                  ))}
                </select>
              </div>

              {/* Quarter & Work Type */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Quarter *</label>
                  <select
                    value={form.quarter}
                    onChange={e => setForm({ ...form, quarter: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", outline: "none" }}
                    required
                  >
                    {quarterOptions.map(q => (
                      <option key={q.key} value={q.key}>{q.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Work Type *</label>
                  <select
                    value={form.workType}
                    onChange={e => setForm({ ...form, workType: e.target.value, priority: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", outline: "none", background: "#fff" }}
                  >
                    <option value="BUSINESS_WORK">Business Work</option>
                    <option value="UPSKILLING">Upskilling</option>
                  </select>
                </div>
              </div>

              {/* Target / Project Title */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                  {(form.workType || "").toUpperCase().includes("UPSKILL") ? "Project Title (Optional)" : "Target Title / Project *"}
                </label>
                <input
                  type="text"
                  placeholder={(form.workType || "").toUpperCase().includes("UPSKILL")
                    ? "e.g. Lead Cloud Architecture Mastery / AI Engineering (Optional)"
                    : "e.g. Lead Cloud Architecture Migration & Backend Optimization"}
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", outline: "none" }}
                  required={!(form.workType || "").toUpperCase().includes("UPSKILL")}
                />
              </div>

              {/* Description / Scope / Skills */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                  {(form.workType || "").toUpperCase().includes("UPSKILL") ? "Description About What Skills They Are Learning *" : "Work Scope & Responsibilities"}
                </label>
                <textarea
                  rows={3}
                  placeholder={(form.workType || "").toUpperCase().includes("UPSKILL")
                    ? "Describe what skills, courses, certifications, or technologies they will learn this quarter..."
                    : "Describe the main objectives, project scope, and work expectations for this quarter..."}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.82rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                />
              </div>

              {/* Key Deliverables & KPIs (Only for Business Work) */}
              {!(form.workType || "").toUpperCase().includes("UPSKILL") && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                    Key Deliverables & KPIs
                  </label>
                  <textarea
                    rows={2}
                    placeholder="e.g. 1. Complete phase 1 rollout by Feb 15&#10;2. Achieve 99.9% uptime and zero critical bugs"
                    value={form.kpi}
                    onChange={e => setForm({ ...form, kpi: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.82rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                  />
                </div>
              )}

              {/* Due Date & Status */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Target Due Date</label>
                  <input
                    type="date"
                    value={form.dueDate}
                    onChange={e => setForm({ ...form, dueDate: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", outline: "none" }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", outline: "none" }}
                  >
                    <option value="ASSIGNED">Assigned</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="REVIEWED">Reviewed & Appraised</option>
                  </select>
                </div>
              </div>

              {/* Sticky Actions Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: 6,
                  borderTop: "1px solid #F1F5F9",
                  paddingTop: 12,
                  paddingBottom: 2,
                  position: "sticky",
                  bottom: -18,
                  background: "#fff",
                  zIndex: 10
                }}
              >
                <button
                  type="button"
                  onClick={() => setAssignModal({ isOpen: false, target: null, defaultUsername: "" })}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", fontSize: "0.8rem", fontWeight: 600, color: "#475569", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#2563EB", fontSize: "0.82rem", fontWeight: 700, color: "#fff", cursor: "pointer", opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? "Saving..." : (assignModal.target ? "Update Target" : "Assign Work")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── APPRAISAL REVIEW MODAL ── */}
      {reviewModal.isOpen && reviewModal.target && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(2px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 16
        }}>
          <div style={{
            background: "#fff", borderRadius: 14, width: "100%", maxWidth: 500,
            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #E2EEFF", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FAF5FF" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon.Award size={18} color="#7C3AED" />
                <h4 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Quarterly Performance Appraisal</h4>
              </div>
              <button onClick={() => setReviewModal({ isOpen: false, target: null })} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "1.1rem" }}>✕</button>
            </div>

            <form onSubmit={handleSubmitReview} style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Target info */}
              <div style={{ padding: "10px 12px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0" }}>
                <div style={{ fontSize: "0.74rem", color: "#64748B" }}>Employee: <strong>{reviewModal.target.employeeName || reviewModal.target.username}</strong> ({reviewModal.target.quarter})</div>
                <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{reviewModal.target.title}</div>
              </div>

              {/* Star Rating */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Performance Rating (1 - 5 Stars) *</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      style={{
                        background: "none", border: "none", cursor: "pointer", fontSize: "1.6rem", padding: 0,
                        color: star <= reviewForm.rating ? "#F59E0B" : "#CBD5E1",
                        transition: "transform 0.1s"
                      }}
                    >
                      ★
                    </button>
                  ))}
                  <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#B45309", marginLeft: 6 }}>
                    {RATING_LABELS[reviewForm.rating] || ""}
                  </span>
                </div>
              </div>

              {/* Feedback & Appraisal Notes */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Appraisal Notes & Feedback</label>
                <textarea
                  rows={4}
                  placeholder="Provide detailed feedback on the employee's work, goal achievements, strengths, and areas for improvement..."
                  value={reviewForm.feedback}
                  onChange={e => setReviewForm({ ...reviewForm, feedback: e.target.value })}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.82rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6, borderTop: "1px solid #F1F5F9", paddingTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setReviewModal({ isOpen: false, target: null })}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", fontSize: "0.8rem", fontWeight: 600, color: "#475569", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#7C3AED", fontSize: "0.82rem", fontWeight: 700, color: "#fff", cursor: "pointer", opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? "Submitting..." : "Submit Appraisal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteModal.isOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(2px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 16
        }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, padding: "20px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
            <h4 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Delete Target?</h4>
            <p style={{ fontSize: "0.82rem", color: "#64748B", marginTop: 8 }}>
              Are you sure you want to delete the target <strong>"{deleteModal.title}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button
                onClick={() => setDeleteModal({ isOpen: false, targetId: null, title: "" })}
                style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTarget}
                disabled={submitting}
                style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#DC2626", color: "#fff", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
              >
                {submitting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TargetsAppraisalsView;
